import { type ServiceParams, unwrapJson } from "../client";
import type {
  FrameworkAlignmentListResponse,
  MethodologyConfigResponse,
  MethodologyConfigUpdate,
  RecomputeRequest,
  RiskDashboardResponse,
  RiskScoreListResponse,
  RiskScoreSnapshotResponse,
  RiskSignalsFilters,
  RiskSnapshotsFilters,
  RiskFramework,
  ScopeType,
  SignalEventListResponse,
} from "./types";

/**
 * Risk service — wraps ``/v1/governance/risk/*`` tenant endpoints.
 *
 * Snapshots / signals / frameworks / methodology are readable by
 * any authenticated tenant user. Recompute + methodology PATCH
 * require tenant admin role; the backend gates that at the
 * dependency layer, the FE surfaces the controls but they 403 for
 * non-admin roles.
 *
 * Note: there is no ``GET /snapshots/{id}`` detail endpoint —
 * snapshot detail in the UI is sourced from the row returned by
 * ``listSnapshots``. Every score + delta + signal_evidence is on
 * that response.
 */
const Risk = ({ url, config }: ServiceParams) => {
  const base = `${url}/v1/governance/risk`;

  const buildQuery = (filters?: Record<string, unknown>) => {
    const params = new URLSearchParams();
    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      }
    }
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  };

  return {
    async getDashboard(
      params: { scope_type?: ScopeType; workspace_id?: string } = {}
    ): Promise<RiskDashboardResponse> {
      const response = await fetch(
        `${base}/dashboard${buildQuery(params as Record<string, unknown>)}`,
        { method: "GET", headers: config() }
      );
      return unwrapJson<RiskDashboardResponse>(response);
    },

    async listSnapshots(
      filters: RiskSnapshotsFilters = {}
    ): Promise<RiskScoreListResponse> {
      const response = await fetch(
        `${base}/snapshots${buildQuery(filters as Record<string, unknown>)}`,
        { method: "GET", headers: config() }
      );
      return unwrapJson<RiskScoreListResponse>(response);
    },

    async getLatestSnapshot(
      params: {
        scope_type?: ScopeType;
        workspace_id?: string;
        asset_id?: string;
      } = {}
    ): Promise<RiskScoreSnapshotResponse> {
      const response = await fetch(
        `${base}/snapshots/latest${buildQuery(
          params as Record<string, unknown>
        )}`,
        { method: "GET", headers: config() }
      );
      return unwrapJson<RiskScoreSnapshotResponse>(response);
    },

    async listFrameworks(
      params: {
        scope_type?: ScopeType;
        workspace_id?: string;
        framework?: RiskFramework;
        latest_only?: boolean;
      } = {}
    ): Promise<FrameworkAlignmentListResponse> {
      const response = await fetch(
        `${base}/frameworks${buildQuery(params as Record<string, unknown>)}`,
        { method: "GET", headers: config() }
      );
      return unwrapJson<FrameworkAlignmentListResponse>(response);
    },

    async listSignals(
      filters: RiskSignalsFilters = {}
    ): Promise<SignalEventListResponse> {
      const response = await fetch(
        `${base}/signals${buildQuery(filters as Record<string, unknown>)}`,
        { method: "GET", headers: config() }
      );
      return unwrapJson<SignalEventListResponse>(response);
    },

    async recompute(
      body: RecomputeRequest = {}
    ): Promise<RiskScoreSnapshotResponse> {
      const response = await fetch(`${base}/recompute`, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(body),
      });
      return unwrapJson<RiskScoreSnapshotResponse>(response);
    },

    async getMethodology(): Promise<MethodologyConfigResponse> {
      const response = await fetch(`${base}/methodology`, {
        method: "GET",
        headers: config(),
      });
      return unwrapJson<MethodologyConfigResponse>(response);
    },

    async updateMethodology(
      body: MethodologyConfigUpdate
    ): Promise<MethodologyConfigResponse> {
      const response = await fetch(`${base}/methodology`, {
        method: "PATCH",
        headers: config(),
        body: JSON.stringify(body),
      });
      return unwrapJson<MethodologyConfigResponse>(response);
    },
  };
};

export default Risk;
